import * as Sentry from '@sentry/react'
import { ErrorEvent, EventHint } from '@sentry/react'
import axios from 'axios'
import { Environment } from './utils/environment'
import { sanitizeAxiosError } from './utils/axiosUtils'

/**
 * Error messages that are noise, not defects, and are therefore dropped before they reach Sentry.
 *
 * The IndexedDB entries need the explanation. Browsers close a page's IndexedDB connection as soon
 * as the page is backgrounded or unloaded — routine on iOS — and every Firebase SDK reads its cache
 * through promises it starts and never awaits (`void this.prepareAndBeginRealtimeHttpStream()`,
 * the sibling reads in `StorageCache.loadFromStorage`). A read that loses that race therefore
 * arrives as an unhandled rejection that no call site of ours can catch, describing a page that is
 * already gone (MD-1430). The app itself never touches IndexedDB, so no first-party failure can
 * hide behind these patterns; that stops holding the day we store anything there ourselves.
 */
export const IGNORED_ERROR_MESSAGES: RegExp[] = [
    // Firebase Auth internal errors that don't affect functionality
    /@firebase\/auth.*INTERNAL ASSERTION FAILED/,
    /INTERNAL ASSERTION FAILED: Pending promise was never set/,
    /INTERNAL ASSERTION FAILED.*firebase.*auth/i,
    // Chromium's and WebKit's wording for a transaction on a closing IndexedDB connection
    /Failed to execute '\w+' on 'IDBDatabase': The database connection is closing/,
    // Firefox's wording for the same condition
    /IDBDatabase\.\w+: Can't start a transaction on a closed database/,
]

export const init_sentry = (environment: Environment) => {
    if (environment === 'local') return

    const dsnConfig = {
        production: 'https://3572c0b18d7ae3ca63113de49263bbb3@o4508834184232960.ingest.de.sentry.io/4508846972993616',
        development: 'https://61eb08fe628f97eff2ea213a577b696e@o4508834184232960.ingest.de.sentry.io/4508847049146448',
        local: 'https://a2270a25b637e158986ad89880120e9f@o4508834184232960.ingest.de.sentry.io/4508846769111120',
    }

    Sentry.init({
        dsn: dsnConfig[environment],
        ignoreErrors: IGNORED_ERROR_MESSAGES,
        integrations: [
            Sentry.breadcrumbsIntegration({
                console: false,
                dom: true,
                fetch: true,
                history: true,
                xhr: true,
            }),
            Sentry.browserTracingIntegration(),
            Sentry.captureConsoleIntegration({
                levels: ['error'],
            }),
            Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
        ],
        enableLogs: true,
        tracesSampleRate: 0.1,
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/medendo-frontend\.(prod|development)\.medendo\.dev/,
            /^https:\/\/workspace\.medendo\.com/,
            /^https:\/\/workspace\.medendo\.dev/,
        ],
        release: import.meta.env.VITE_VERSION,
        beforeSend: handleSentryBeforeSend,
    })

    setup_console_breadcrumbs()
}

function setup_console_breadcrumbs() {
    const originalWarn = console.warn
    const originalError = console.error

    console.warn = (message, ...args) => {
        sanitizeAxiosError(message)
        args.forEach(sanitizeAxiosError)

        Sentry.addBreadcrumb({
            category: 'console',
            level: 'warning',
            message: String(message),
            data: {
                args,
                url: window.location.href,
                stack: new Error().stack, // capture current stack
            },
        })
        originalWarn.apply(console, [message, ...args])
    }

    console.error = (message, ...args) => {
        sanitizeAxiosError(message)
        args.forEach(sanitizeAxiosError)

        let stack: string | undefined = undefined

        // if first arg is an Error, use its stack
        if (message instanceof Error) {
            stack = message.stack
        } else if (args.length && args[0] instanceof Error) {
            stack = args[0].stack
        } else {
            stack = new Error().stack
        }

        Sentry.addBreadcrumb({
            category: 'console',
            level: 'error',
            message: String(message),
            data: {
                args,
                url: window.location.href,
                stack,
            },
        })

        originalError.apply(console, [message, ...args])
    }
}

// A backend error detail is a sentence, not a document. Cap it so a body that is not what we expect
// (an HTML page from an intercepting proxy, say) cannot bloat the event.
const MAX_RESPONSE_DETAIL_LENGTH = 512

/**
 * Summarizes an error response body as a single short string for the Sentry event.
 *
 * Every handled backend error answers with `{"detail": ...}` (see the platform API's
 * `error_handlers.py`), so that field is the only part worth carrying. A body of any other shape
 * either came from an unhandled path or from something that is not the backend at all, and a capped
 * snippet is enough to recognise that.
 * @param data The parsed response body of the failed request.
 * @returns The detail as a capped string, or undefined if there is nothing to report.
 */
function describeResponseBody(data: unknown): string | undefined {
    if (data === null || data === undefined || data === '') return undefined

    const detail = typeof data === 'object' && 'detail' in data ? (data as { detail: unknown }).detail : data
    if (detail === null || detail === undefined) return undefined

    let text: string
    try {
        text = typeof detail === 'string' ? detail : JSON.stringify(detail)
    } catch {
        // A circular body is not worth reporting on, but it must not take the event down with it.
        return undefined
    }

    return text ? text.slice(0, MAX_RESPONSE_DETAIL_LENGTH) : undefined
}

/**
 * Enriches a Sentry event for a failed API request with the endpoint, status and backend detail.
 *
 * The request that failed and what the backend said about it are the whole story of an API error,
 * and neither is in the default event: the exception value is a generic "Request failed with status
 * code 500" and the response body is not attached at all (MD-1486).
 * @param event The Sentry event about to be sent.
 * @param hint The event hint, whose `originalException` is inspected for an Axios error.
 * @returns The event, enriched if it describes a failed API request and unchanged otherwise.
 */
export function handleSentryBeforeSend(event: ErrorEvent, hint: EventHint) {
    // `axios.isAxiosError` tests the `isAxiosError` marker on the instance. A `constructor.name`
    // check looks equivalent but is dead in a production build, where the class name is minified.
    if (!axios.isAxiosError(hint?.originalException)) {
        return event
    }

    const axiosError = hint.originalException
    const cfg = axiosError.config
    const res = axiosError.response
    const method = (cfg?.method ?? 'GET').toUpperCase()

    const statusPart = res?.status ? String(res.status) : 'unknown'

    // Tags for quick filters
    event.tags = {
        ...event.tags,
        method,
        endpoint: cfg?.url,
        status: res?.status ? String(res.status) : undefined,
        code: axiosError.code,
    }
    // Flat scalars, not the error object: Sentry normalizes extra data to a depth of 3, which
    // collapses a nested `response.data` to "[Object]" and loses exactly what we came for.
    event.extra = {
        ...event.extra,
        request_method: method,
        request_url: cfg?.url,
        response_status: res?.status,
        response_detail: describeResponseBody(res?.data),
    }

    // Make the event title readable
    const ep = cfg?.url ?? 'unknown'
    event.message = `[API] ${method} ${ep} failed ${statusPart}`
    if (event.exception?.values?.length) {
        event.exception.values[0].type = 'AxiosRequestError'
        event.exception.values[0].value = `${method} ${ep} → ${statusPart}`
    }

    return event
}
